import { useState } from "react";
import { useAuditLogs, useAuditLogsCount, useAuditLogTables } from "@/hooks/use-audit-logs";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;

function formatTime(ts: string): string {
  return new Date(ts).toLocaleString();
}

export default function AuditLogsPage() {
  const [page, setPage] = useState(0);
  const [tableFilter, setTableFilter] = useState("");
  const [opFilter, setOpFilter] = useState("");
  const skip = page * PAGE_SIZE;

  const params = {
    skip,
    limit: PAGE_SIZE,
    ...(tableFilter ? { table_name: tableFilter } : {}),
    ...(opFilter ? { operation: opFilter } : {}),
  };

  const { data: logs, isLoading } = useAuditLogs(params);
  const { data: countData } = useAuditLogsCount(
    tableFilter || opFilter ? { table_name: tableFilter || undefined, operation: opFilter || undefined } : undefined
  );
  const { data: tables } = useAuditLogTables();
  const total = countData?.count ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const opLabels: Record<string, string> = {
    CREATE: "bg-green-100 text-green-800",
    UPDATE: "bg-blue-100 text-blue-800",
    DELETE: "bg-red-100 text-red-800",
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Audit Logs</h1>
        <p className="text-sm text-muted-foreground mt-1">{total} entries (last 7 days)</p>
      </div>

      <div className="flex gap-3">
        <Select
          value={tableFilter}
          onChange={(e) => { setTableFilter(e.target.value); setPage(0); }}
          className="w-48"
        >
          <option value="">All Tables</option>
          {tables?.map((t) => <option key={t} value={t}>{t}</option>)}
        </Select>
        <Select
          value={opFilter}
          onChange={(e) => { setOpFilter(e.target.value); setPage(0); }}
          className="w-36"
        >
          <option value="">All Ops</option>
          <option value="CREATE">Create</option>
          <option value="UPDATE">Update</option>
          <option value="DELETE">Delete</option>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th className="px-3 py-2 font-medium">Timestamp</th>
              <th className="px-3 py-2 font-medium">Entity</th>
              <th className="px-3 py-2 font-medium">Record</th>
              <th className="px-3 py-2 font-medium">Operation</th>
              <th className="px-3 py-2 font-medium">Changed Fields</th>
              <th className="px-3 py-2 font-medium">IP</th>
              <th className="px-3 py-2 font-medium">Endpoint</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Loading...</td></tr>
            )}
            {!isLoading && (!logs || logs.length === 0) && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No audit log entries</td></tr>
            )}
            {logs?.map((log) => (
              <tr key={log.id} className="border-b border-border hover:bg-muted/30">
                <td className="px-3 py-2 text-xs whitespace-nowrap">{formatTime(log.timestamp)}</td>
                <td className="px-3 py-2 text-xs">{log.table_name}</td>
                <td className="px-3 py-2 text-xs font-mono max-w-[150px] truncate">{log.record_name || log.record_id}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${opLabels[log.operation] || "bg-gray-100 text-gray-800"}`}>
                    {log.operation}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs max-w-[200px] truncate">
                  {log.changed_fields?.join(", ") || "-"}
                </td>
                <td className="px-3 py-2 text-xs font-mono">{log.ip_address || "-"}</td>
                <td className="px-3 py-2 text-xs font-mono max-w-[150px] truncate">{log.endpoint || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft size={14} /> Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
              Next <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}